import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
// @mui
import { Box, Stack, Divider, Button, Typography } from '@mui/material';
import { useTheme, styled } from '@mui/material/styles';
// routes
import { PATH } from '../../routes/paths';
// hooks
import useAuth from '../../hooks/useAuth';
import useLocales from '../../hooks/useLocales';
// components
import Iconify from '../../components/Iconify';
import LoadingLogo from '../../components-custom/LoadingLogo';
import Image from '../../components/Image';
// utils
import { errorHandler } from '../../utils/errorHandler';
import { track } from '../../components-custom/Analytics';

// ----------------------------------------------------------------------

function useQuery() {
  const location = useLocation();
  let search = location.search || location.hash;
  if (search.startsWith('#')) search = `?${search.slice(1, search.length)}`;
  const query = new URLSearchParams(search);
  return {
    access_token: query.get('access_token'),
    state: query.get('state'),
  };
}

const GoogleButton = styled(Button)(({ theme }) => ({
  color: theme.palette.getContrastText(theme.palette.mode === 'dark' ? '#4285F4' : '#ffffff'),
  backgroundColor: theme.palette.mode === 'dark' ? '#4285F4' : '#ffffff',
  borderWidth: 0.5,
  borderStyle: 'solid',
  borderColor: theme.palette.mode === 'dark' ? '#4285F4' : '#dadce0',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? '#4285F4' : '#ffffff',
  },
}));

const FacebookButton = styled(Button)(({ theme }) => ({
  color: theme.palette.getContrastText('#1877f2'),
  backgroundColor: '#1877f2',
  '&:hover': {
    backgroundColor: '#1877f2',
  },
}));

// ----------------------------------------------------------------------

SSOIcons.propTypes = {
  register: PropTypes.bool,
};

export function SSOIcons({ register }) {
  const { getSSOUrl } = useAuth();
  const { translate } = useLocales();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  const handleSso = async (platform) => {
    try {
      const request = await getSSOUrl(platform);
      track.event(`SSOIcons.login.${platform}`);
      window.location.replace(request.url);
    } catch (error) {
      enqueueSnackbar(translate('auth.sso.message.button.error'), { variant: 'error' });
    }
  };

  return (
    <Stack spacing={2} sx={{ paddingTop: 2 }} alignItems="center">
      <Divider sx={{ borderStyle: 'dashed' }} flexItem>
        {translate('auth.sso.or')}
      </Divider>
      <Stack direction="column" alignItems="center" justifyContent="center" spacing={2} maxWidth={264} width={1}>
        <GoogleButton fullWidth variant="contained" color="inherit" disableRipple onClick={() => handleSso('google')}>
          <Stack
            direction={theme.direction === 'rtl' ? 'row-reverse' : 'row'}
            alignItems="center"
            justifyContent="flex-start"
            spacing={2}
            width={1}
          >
            <Image src="/static/sso/g-logo.png" sx={{ height: 32, p: 0.5, backgroundColor: '#ffffff' }} />
            <Typography variant="subtitle1">{register ? 'Sign up with Google' : 'Sign in with Google'}</Typography>
          </Stack>
        </GoogleButton>
        <FacebookButton fullWidth variant="contained" color="inherit" onClick={() => handleSso('facebook')}>
          <Stack
            direction={theme.direction === 'rtl' ? 'row-reverse' : 'row'}
            alignItems="center"
            justifyContent="flex-start"
            spacing={2}
            width={1}
          >
            <Iconify icon={'ic:twotone-facebook'} color="#FFFFFF" width={32} height={32} />
            <Typography variant="subtitle1">{register ? 'Sign up with Facebook' : 'Sign in with Facebook'}</Typography>
          </Stack>
        </FacebookButton>
      </Stack>
    </Stack>
  );
}

export function SSOForm() {
  const { loginSSO } = useAuth();
  const { translate, currentLang } = useLocales();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const query = useQuery();

  useEffect(() => {
    const checkCode = async (query) => {
      try {
        await loginSSO({ ...query, lang: currentLang.value });
        track.event('SSOForm.login');
        enqueueSnackbar(translate('auth.sso.message.login.success'), { variant: 'success' });
        navigate(PATH.root);
      } catch (error) {
        track.event('SSOForm.login.error');
        enqueueSnackbar(errorHandler(error, translate('auth.sso.message.login.error')), { variant: 'error' });
        navigate(PATH.root);
      }
    };
    checkCode(query);
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box sx={{ my: 5, mx: 'auto', textAlign: 'center' }}>
      <LoadingLogo />
    </Box>
  );
}

export default { SSOIcons, SSOForm };
